import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DocumentLetterType } from '@app/core/model/document-letter-type.model';
import { DocumentTypesList } from '@app/documents/models';
import { SharedLineSelectTableConfig } from '@app/shared/models/shared-line-select-table-config.model';
import { SharedDocumentsService } from '@app/shared/services/shared-documents.service';
import { ShellService } from '@mynexus/mfe-core';
import { ProductNames, TableColumn, TenantNames } from '@mynexus/mynexus-ui-lib';
import { PRODUCT_LINE_STATUS } from '@products/dmepos/constants/product-case';
import { Observable, map, of } from 'rxjs';
import { DocumentReferenceDTO } from './send-fax/send-fax-dmepos.service';
import { ICoreAuthorizationDTO } from '@app/core/model/core-authorization.model';
import { IServicingProviderDTO } from '../models';

@Injectable({
  providedIn: 'root',
})
export class DocumentsUploadLetterDmePosService extends SharedDocumentsService {

  constructor(
    http: HttpClient,
    shellService: ShellService,
  ) {
    super(http, shellService, ProductNames.DMEPOS);
  }

  isSingleLineSelection(): boolean {
    return false;
  }

  getLetterTypes(tenantId: string, authorizationByProductId: string): Observable<DocumentLetterType[]> {
    return this.http.get<DocumentReferenceDTO[]>(`${this.API_URL}/tenants/${tenantId}/authorization/${authorizationByProductId}/DocumentReferences?reference=Fax&entity=DocumentType`)
    .pipe(
      map((references: DocumentReferenceDTO[]) => references
        .filter(x => x.description.includes('Letter'))
        .sort((a,b) => a.sortOrder - b.sortOrder)
        .map(x => {
          return <DocumentLetterType>{
            documentDisplayName: x.description,
            enableLineSelection: true,
            isEligibleForUpload: true,
          }})),
    );
  }

  getLineTableConfig(): TableColumn[] {
    return [
      new TableColumn('Case Number', 'caseNumber', '10rem'),
      new TableColumn('Product Number', 'lineNumber', '9rem'),
      new TableColumn('Date/Time Created', 'createdDate', '9rem'),
      new TableColumn('Date/Time Request Received', 'requestReceivedDate', '9rem'),
      new TableColumn('Urgency', 'urgency', '9rem'),
      new TableColumn('HCPCS', 'hcpcs', '8rem'),
      new TableColumn('Requested Units', 'requestedAmount', '8rem'),
      new TableColumn('Approved Units', 'approvedAmount', '8rem'),
      new TableColumn('Effective From', 'effectiveFrom', '8rem'),
      new TableColumn('Effective To', 'effectiveTo', '8rem'),
      new TableColumn('Status', 'status', '8rem'),
      // The Decision column name was changed to Processed under CR-237287. The difference between column name and DTO property name IS intentional
      new TableColumn('Processed Date', 'decisionDate', '8rem'),
      new TableColumn('Date/Time Urgency Change Downgrade', 'downgradeDate', '8rem'),
      new TableColumn('Date Extension Taken', 'extensionDate', '8rem'),
      new TableColumn('Status Reason', 'statusReason', '8rem'),
    ];
  }

  mapLineTableData(letterType: string, lineDto: object[], tenantName: TenantNames, authorization: ICoreAuthorizationDTO | null, servicingProvider: IServicingProviderDTO | null, authProviderPgpId: string | null) {
    return lineDto
      .reduce((acc: SharedLineSelectTableConfig[], c: object) => {
        const mappedLines: SharedLineSelectTableConfig[] = c['productLines']
          .filter((x: object) => this.filterByLetterType(x, letterType))
          .map((x: object) => this.getLineSelectTableData(c, x));
        return [...acc, ...mappedLines];
      }, []);
  }

  getNoLineMessage(documentType: string, tenantName: TenantNames): string {
    switch (documentType) {
      case DocumentTypesList.PartialDenialLetter: return 'NOTE: This authorization has no product cases with both denied and approved lines to select.';
      default: return '';
    }
  }

  private filterByLetterType(c: object, letterType: string): boolean {
    const status = c['status'];
    const urgencyChange = c['urgencyChange'];
    const isExtension = c['isExtension'];

    switch (letterType) {
      case DocumentTypesList.ApprovalLetter: return status.description === PRODUCT_LINE_STATUS.Approved;
      case DocumentTypesList.IDNLetter: return status.description === PRODUCT_LINE_STATUS.Denied;
      case DocumentTypesList.AdverseBenefitDeterminationLetter: return [PRODUCT_LINE_STATUS.Approved, PRODUCT_LINE_STATUS.Denied].includes(status.description);
      case DocumentTypesList.DismissalLetter: return status.description === PRODUCT_LINE_STATUS.Dismissed;
      case DocumentTypesList.ExpeditedGrievanceLetter: return urgencyChange?.description === 'Downgraded' || isExtension;
      case DocumentTypesList.DecisionTimeframeExtensionLetter: return status.description === PRODUCT_LINE_STATUS.Pending;
      case DocumentTypesList.PartialDenialLetter: return [PRODUCT_LINE_STATUS.Approved, PRODUCT_LINE_STATUS.Denied].includes(status.description);
      default: return false;
    }
  }

  private getLineSelectTableData(caseDto: object, x: object): SharedLineSelectTableConfig {
    return {
      lineId: x['productLineId'],
      caseNumber: caseDto['sequenceNumber'],
      lineNumber: x['sequenceNumber'],
      createdDate: x['createdDate'],
      requestReceivedDate: x['requestReceivedDate'],
      urgency: x['urgency'] ? x['urgency']['description'] : '',
      hcpcs: x['hcpcsByModifier'] ? x['hcpcsByModifier']['hcpcs']['code'] + (x['hcpcsByModifier']['hcpcsModifier']?.['description'] || '') : '',
      requestedAmount: x['unitsRequested'],
      approvedAmount: x['unitsApproved'],
      effectiveFrom: x['effectiveStartDate'],
      effectiveTo: x['effectiveEndDate'],
      status: x['status']['description'],
      decisionDate: x['decisionDate'],
      downgradeDate: x['urgencyChangeDate'],
      extensionDate: x['extensionTakenDate'],
      statusReason: x['statusReason']['description'],
    }
  }

  getAuthorizationProvidersSummary(tenantId: string, authorizationByProductId: string): Observable<object> {
    return of({});
  }
}
